import { firebaseDB } from "../firebase/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  arrayUnion,
  arrayRemove,
  deleteField,
} from "firebase/firestore";
import { AppUser, AppUserId } from "../../domain/types/AppUser";
import { getFirestoreSafeProviderName } from "./providerUserRepository";
import { ProviderUser } from "../../domain/types/ProviderUser";
import { UserBank } from "../../domain/types/banks/UserBank";

export class FirestoreAppUserRepository {
  async createOrUpdateAppUser(
    appUserId: AppUserId,
    providerName: string,
    providerUserId: string,
    familyId: string,
  ): Promise<void> {
    const { appUserRef, appUserDoc } = await this._fetchAppUser(appUserId);
    const safeProviderName = getFirestoreSafeProviderName(providerName);

    if (appUserDoc.exists()) {
      const existingData = appUserDoc.data() as AppUser;
      const existingProviderUserIds = existingData.providerUserIds || {};
      const existingUserBankAccounts = existingData.userBankAccounts || {};
      const existingFamilyId = existingData.familyId || familyId;

      const updatedProviderUserIds = { ...existingProviderUserIds };
      updatedProviderUserIds[safeProviderName] = [
        ...new Set([
          ...(existingProviderUserIds[safeProviderName] || []),
          providerUserId,
        ]),
      ];

      await updateDoc(appUserRef, {
        providerUserIds: updatedProviderUserIds,
        familyId: existingFamilyId,
        userBankAccounts: existingUserBankAccounts,
      });
    } else {
      const newAppUser: AppUser = {
        appUserId,
        familyId,
        providerUserIds: {
          [safeProviderName]: [providerUserId],
        },
        primaryProviderUserId: providerUserId,
        userBankAccounts: {},
      };
      await setDoc(appUserRef, newAppUser);
    }
  }

  async addUserBankAccount(
    appUserId: AppUserId,
    userBankId: string,
    bankAccountId: string,
  ) {
    const appUserRef = doc(firebaseDB, "appUsers", appUserId);
    const appUserDoc = await getDoc(appUserRef);

    if (appUserDoc.exists()) {
      await updateDoc(appUserRef, {
        [`userBankAccounts.${userBankId}`]: arrayUnion(bankAccountId),
      });
    }
  }

  async removeUserBankAccount(
    appUserId: AppUserId,
    userBankId: string,
    bankAccountId: string,
  ) {
    const appUserRef = doc(firebaseDB, "appUsers", appUserId);
    const appUserDoc = await getDoc(appUserRef);

    if (appUserDoc.exists()) {
      await updateDoc(appUserRef, {
        [`userBankAccounts.${userBankId}`]: arrayRemove(bankAccountId),
      });
    }
  }

  async getUserBankAccounts(
    appUserId: AppUserId,
    userBankId?: string,
  ): Promise<UserBank[]> {
    const appUser = await this.findByAppUserId(appUserId);
    if (!appUser) return [];

    const userBankAccountsCollection = collection(
      firebaseDB,
      "userBankAccounts",
    );
    let q = query(
      userBankAccountsCollection,
      where("appUserId", "==", appUserId),
    );

    if (userBankId) {
      q = query(q, where("userBankId", "==", userBankId));
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => doc.data() as UserBank);
  }

  async findByAppUserId(appUserId: AppUserId): Promise<AppUser | null> {
    const appUserRef = doc(firebaseDB, "appUsers", appUserId);
    const appUserDoc = await getDoc(appUserRef);
    return appUserDoc.exists() ? (appUserDoc.data() as AppUser) : null;
  }

  async findByProviderUserId(
    providerName: string,
    providerUserId: string,
  ): Promise<AppUser | null> {
    const appUserRef = collection(firebaseDB, "appUsers");
    const q = query(
      appUserRef,
      where(
        "providerUserIds." + getFirestoreSafeProviderName(providerName),
        "array-contains",
        providerUserId,
      ),
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.empty
      ? null
      : (querySnapshot.docs[0].data() as AppUser);
  }

  async providerUsers(appUserId: AppUserId): Promise<ProviderUser[]> {
    const appUser = await this.findByAppUserId(appUserId);
    if (!appUser) return [];

    const providerUsersCollection = collection(firebaseDB, "providerUsers");
    const providerUserIds = Object.values(appUser.providerUserIds).flat();

    if (providerUserIds.length === 0) return [];

    const q = query(
      providerUsersCollection,
      where("uid", "in", providerUserIds),
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => doc.data() as ProviderUser);
  }

  async setPrimaryProviderUser(
    appUserId: AppUserId,
    providerUserId: string,
  ): Promise<void> {
    const { appUserRef } = await this._fetchAppUser(appUserId);
    await updateDoc(appUserRef, {
      primaryProviderUserId: providerUserId,
    });
  }

  async addCalendarEvent(appUserId: string, streamId: string, eventId: string) {
    const userRef = doc(firebaseDB, "appUsers", appUserId);
    await updateDoc(userRef, {
      [`calendarEvents.${streamId}`]: eventId,
    });
    console.log("Calendar event added:", eventId);
  }

  async removeCalendarEvent(appUserId: string, streamId: string) {
    const userRef = doc(firebaseDB, "appUsers", appUserId);

    await updateDoc(userRef, {
      [`calendarEvents.${streamId}`]: deleteField(),
    });
    console.log("Calendar event removed:", streamId);
  }

  async getCalendarEventId(
    appUserId: string,
    streamId: string,
  ): Promise<string | null> {
    const userRef = doc(firebaseDB, "appUsers", appUserId);
    const docSnap = await getDoc(userRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as AppUser;
      return data.calendarEvents?.[streamId] || null;
    } else {
      return null;
    }
  }

  async getCalendarEvents(appUserId: string): Promise<string[]> {
    const userRef = doc(firebaseDB, "appUsers", appUserId);
    const docSnap = await getDoc(userRef);
    if (docSnap.exists()) {
      return docSnap.data().calendarEvents || [];
    }
    return [];
  }

  private async _fetchAppUser(appUserId: AppUserId) {
    const appUserRef = doc(firebaseDB, "appUsers", appUserId);
    const appUserDoc = await getDoc(appUserRef);
    return { appUserRef, appUserDoc };
  }
}
